// src/lib/api/jobs/checklists.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling } from '../shared/errors'
import { validateRequired } from '../shared/validation'

/**
 * Get job checklists
 * Migrated from supabaseApi.js lines 295-309 (approximate)
 */
const _getJobChecklists = async (companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return { data: [] }
  }

  try {
    const { data, error } = await supabase
      .from('job_checklists')
      .select('*')
      .eq('company_id', resolvedCompanyId)
      .order('created_at', { ascending: false })

    // Legacy RLS handling - should not be needed after policy fix
    if (error && error.code === 'PGRST116') {
      console.warn(`[Checklists] Legacy RLS issue - check if RLS policy fix has been applied`)
      return { data: [] }
    }

    if (error) throw error
    return { data: data || [] }
  } catch (error) {
    // Legacy error handling - should not trigger after policy fix
    console.error(`[Checklists] Unexpected error accessing job checklists for company ${resolvedCompanyId}:`, error.message)
    throw error // Re-throw after policy fix to surface real issues
  }
}

/**
 * Get job checklist items
 * Migrated from supabaseApi.js lines 311-325 (approximate)
 */
const _getJobChecklistItems = async (companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return { data: [] }
  }

  const { data, error } = await supabase
    .from('job_checklist_items')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .order('position', { ascending: true })

  if (error) throw error
  return { data: data || [] }
}

/**
 * Create a checklist for a job
 * Migrated from supabaseApi.js lines 752-770
 */
const _createChecklist = async (jobId) => {
  validateRequired(jobId, 'Job ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const userId = await getValidatedUserId()
  if (!userId) throw new Error('Not authenticated')

  try {
    const { data, error } = await supabase
      .from('job_checklists')
      .insert([{
        company_id: companyId,
        job_id: jobId,
        title: 'Job Checklist',
        created_by: userId
      }])
      .select()
      .single()
    
    // Legacy RLS handling - should not be needed after policy fix
    if (error && error.code === 'PGRST116') {
      console.warn(`[Checklists] Legacy RLS issue creating checklist - check if RLS policy fix has been applied`)
      throw new Error('RLS policy needs to be updated for companies without employees')
    }
    
    if (error) throw error
    return { ...data, items: [] }
  } catch (error) {
    // Re-throw after policy fix to surface real issues
    console.error(`[Checklists] Error creating checklist for job ${jobId}:`, error.message)
    throw error
  }
}

/**
 * Get complete checklist by job ID
 * Migrated from supabaseApi.js lines 772-810
 */
const _getChecklistByJobId = async (jobId) => {
  validateRequired(jobId, 'Job ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  try {
    const { data: checklist, error } = await supabase
      .from('job_checklists')
      .select('id, job_id')
      .eq('company_id', companyId)
      .eq('job_id', jobId)
      .single()

    // Legacy RLS handling - should not be needed after policy fix
    if (error && error.code === 'PGRST116') {
      console.warn(`[Checklists] Legacy RLS issue accessing job_checklists for job ${jobId} - check if RLS policy fix has been applied`)
      return null
    }

    if (error) throw error
    if (!checklist) return null

    const { data: items } = await supabase
      .from('job_checklist_items')
      .select('id, title, is_done, position')
      .eq('company_id', companyId)
      .eq('checklist_id', checklist.id)
      .order('position', { ascending: true })

    const itemIds = (items || []).map(i => i.id)
    const { data: attachments } = itemIds.length > 0 ? await supabase
      .from('job_checklist_attachments')
      .select('id, item_id, path, mime_type, size_bytes, created_at')
      .eq('company_id', companyId)
      .in('item_id', itemIds) : { data: [] }

    // Map signed URLs
    const signed = await Promise.all((attachments || []).map(async a => {
      try {
        const { data: signedUrl } = await supabase.storage
          .from('checklist-images')
          .createSignedUrl(a.path, 60 * 60)
        return { ...a, url: signedUrl?.signedUrl || null }
      } catch { return { ...a, url: null } }
    }))

    return {
      ...checklist,
      items: (items || []).map(i => ({
        ...i,
        attachments: signed.filter(a => a.item_id === i.id)
      }))
    }
  } catch (error) {
    // Re-throw after policy fix to surface real issues
    console.error(`[Checklists] Error accessing job checklist for job ${jobId}:`, error.message)
    throw error
  }
}

/**
 * Add checklist item
 * Migrated from supabaseApi.js lines 812-830
 */
const _addChecklistItem = async ({ checklistId, title, position }) => {
  validateRequired(checklistId, 'Checklist ID')
  validateRequired(title, 'Title')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const userId = await getValidatedUserId()
  if (!userId) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('job_checklist_items')
    .insert([{
      company_id: companyId,
      checklist_id: checklistId,
      title,
      position,
      created_by: userId
    }])
    .select()
    .single()
    
  if (error) throw error
  return { ...data, attachments: [] }
}

/**
 * Update checklist item
 * Migrated from supabaseApi.js lines 832-844
 */
const _updateChecklistItem = async ({ itemId, title }) => {
  validateRequired(itemId, 'Item ID')
  validateRequired(title, 'Title')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const { data, error } = await supabase
    .from('job_checklist_items')
    .update({ title, updated_at: new Date().toISOString() })
    .eq('id', itemId)
    .eq('company_id', companyId)
    .select()
    .single()
    
  if (error) throw error
  return data
}

/**
 * Toggle checklist item completion
 * Migrated from supabaseApi.js lines 846-858
 */
const _toggleChecklistItem = async ({ itemId, is_done }) => {
  validateRequired(itemId, 'Item ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const { data, error } = await supabase
    .from('job_checklist_items')
    .update({ is_done, updated_at: new Date().toISOString() })
    .eq('id', itemId)
    .eq('company_id', companyId)
    .select()
    .single()
    
  if (error) throw error
  return data
}

/**
 * Batch update checklist items
 */
const _batchUpdateChecklistItems = async (updates) => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const results = []
  for (const update of updates) {
    const { data, error } = await supabase
      .from('job_checklist_items')
      .update(update.data)
      .eq('id', update.itemId)
      .eq('company_id', companyId)
      .select()
      .single()
    
    if (error) {
      console.error('Error updating checklist item:', error)
      results.push({ error })
    } else {
      results.push({ data })
    }
  }
  
  return results
}

/**
 * Batch toggle checklist items
 */
const _batchToggleChecklistItems = async (toggles) => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const results = []
  for (const toggle of toggles) {
    const { data, error } = await supabase
      .from('job_checklist_items')
      .update({ is_done: toggle.is_done, updated_at: new Date().toISOString() })
      .eq('id', toggle.itemId)
      .eq('company_id', companyId)
      .select()
      .single()
    
    if (error) {
      console.error('Error toggling checklist item:', error)
      results.push({ error })
    } else {
      results.push({ data })
    }
  }
  
  return results
}

/**
 * Reorder checklist items
 * Migrated from supabaseApi.js lines 1271-1284
 */
const _reorderChecklistItems = async ({ checklistId, orderedItemIds }) => {
  validateRequired(checklistId, 'Checklist ID')
  validateRequired(orderedItemIds, 'Ordered item IDs')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  // Batch updates
  const updates = orderedItemIds.map((id, idx) => (
    supabase.from('job_checklist_items')
      .update({ position: idx, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('company_id', companyId)
  ))
  
  await Promise.all(updates)
  return { success: true }
}

/**
 * Delete checklist item
 * Migrated from supabaseApi.js lines 1285-1307
 */
const _deleteChecklistItem = async (itemId) => {
  validateRequired(itemId, 'Item ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  // Remove attachments from storage first
  const { data: att } = await supabase
    .from('job_checklist_attachments')
    .select('path')
    .eq('company_id', companyId)
    .eq('item_id', itemId)
    
  const paths = (att || []).map(a => a.path)
  if (paths.length > 0) {
    await supabase.storage.from('checklist-images').remove(paths)
  }
  
  const { error } = await supabase
    .from('job_checklist_items')
    .delete()
    .eq('id', itemId)
    .eq('company_id', companyId)
    
  if (error) throw error
  return { success: true }
}

/**
 * Batch delete checklist items
 * Migrated from supabaseApi.js lines 1356-1370
 */
const _batchDeleteChecklistItems = async (deletes) => {
  validateRequired(deletes, 'Delete operations')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const itemIds = deletes.map(d => d.itemId)
  const { error } = await supabase
    .from('job_checklist_items')
    .delete()
    .in('id', itemIds)
    .eq('company_id', companyId)
  
  if (error) throw error
  return { success: true, deleted: deletes.length }
}

/**
 * Upload checklist image
 * Migrated from supabaseApi.js lines 1371-1412
 */
const _uploadChecklistImage = async ({ jobId, itemId, file }) => {
  validateRequired(jobId, 'Job ID')
  validateRequired(itemId, 'Item ID')
  validateRequired(file, 'File')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  // Get current user ID for created_by field
  const userId = await getValidatedUserId()
  if (!userId) throw new Error('No authenticated user found')
  
  const ext = (file.name?.split('.')?.pop() || 'jpg').toLowerCase()
  const key = `checklists/${jobId}/${itemId}/${crypto.randomUUID()}.${ext}`
  
  // Upload to storage
  const { error: uploadError } = await supabase.storage
    .from('checklist-images')
    .upload(key, file, { contentType: file.type, upsert: false })
  
  if (uploadError) throw uploadError
  
  // Insert database record
  const { data, error } = await supabase
    .from('job_checklist_attachments')
    .insert([{
      company_id: companyId,
      item_id: itemId,
      path: key,
      mime_type: file.type || null,
      size_bytes: file.size || null,
      created_by: userId
    }])
    .select()
    .single()
    
  if (error) throw error
  
  // Generate signed URL
  const { data: signedUrl } = await supabase.storage
    .from('checklist-images')
    .createSignedUrl(key, 60 * 60)
    
  return { ...data, url: signedUrl?.signedUrl || null }
}

/**
 * Delete checklist attachment
 * Migrated from supabaseApi.js lines 1413-1444
 */
const _deleteChecklistAttachment = async (attachmentId) => {
  validateRequired(attachmentId, 'Attachment ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  // First get the attachment to get the storage path
  const { data: attachment, error: fetchError } = await supabase
    .from('job_checklist_attachments')
    .select('path')
    .eq('id', attachmentId)
    .eq('company_id', companyId)
    .single()

  if (fetchError) throw fetchError

  // Delete from storage
  if (attachment?.path) {
    await supabase.storage
      .from('checklist-images')
      .remove([attachment.path])
  }

  // Delete from database
  const { error } = await supabase
    .from('job_checklist_attachments')
    .delete()
    .eq('id', attachmentId)
    .eq('company_id', companyId)

  if (error) throw error
  return { success: true }
}

// Export all functions with error handling
export const getJobChecklists = withErrorHandling(_getJobChecklists, 'fetch job checklists', 'job_checklists')
export const getJobChecklistItems = withErrorHandling(_getJobChecklistItems, 'fetch job checklist items', 'job_checklist_items')
export const createChecklist = withErrorHandling(_createChecklist, 'create checklist', 'checklist')
export const getChecklistByJobId = withErrorHandling(_getChecklistByJobId, 'fetch checklist by job', 'checklist')
export const addChecklistItem = withErrorHandling(_addChecklistItem, 'add checklist item', 'checklist_item')
export const updateChecklistItem = withErrorHandling(_updateChecklistItem, 'update checklist item', 'checklist_item')
export const toggleChecklistItem = withErrorHandling(_toggleChecklistItem, 'toggle checklist item', 'checklist_item')
export const batchUpdateChecklistItems = withErrorHandling(_batchUpdateChecklistItems, 'batch update checklist items', 'checklist_items')
export const batchToggleChecklistItems = withErrorHandling(_batchToggleChecklistItems, 'batch toggle checklist items', 'checklist_items')
export const reorderChecklistItems = withErrorHandling(_reorderChecklistItems, 'reorder checklist items', 'checklist_items')
export const deleteChecklistItem = withErrorHandling(_deleteChecklistItem, 'delete checklist item', 'checklist_item')
export const batchDeleteChecklistItems = withErrorHandling(_batchDeleteChecklistItems, 'batch delete checklist items', 'checklist_items')
export const uploadChecklistImage = withErrorHandling(_uploadChecklistImage, 'upload checklist image', 'checklist_attachment')
export const deleteChecklistAttachment = withErrorHandling(_deleteChecklistAttachment, 'delete checklist attachment', 'checklist_attachment')